import { Form, Link, useLocation, useRouteLoaderData } from 'react-router'
import { Button } from '#app/components/ui/button.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { type loader as rootLoader } from '#app/root.tsx'
import { ThemeSwitch } from '#app/routes/resources/theme-switch.tsx'
import {
	PROFILE_HOME_PATH,
	PROGRESS_HOME_PATH,
	STUDY_HOME_PATH,
} from '#app/utils/app-routes.ts'
import { cn } from '#app/utils/misc.tsx'
import { useOptionalUser } from '#app/utils/user.ts'

function isActiveNav(pathname: string, target: string) {
	if (target === STUDY_HOME_PATH) {
		return (
			pathname === STUDY_HOME_PATH ||
			(pathname.startsWith(`${STUDY_HOME_PATH}/`) &&
				!pathname.startsWith(`${PROGRESS_HOME_PATH}/`) &&
				pathname !== PROGRESS_HOME_PATH)
		)
	}
	if (target === PROFILE_HOME_PATH) {
		return (
			pathname === PROFILE_HOME_PATH ||
			pathname.startsWith(`${PROFILE_HOME_PATH}/`) ||
			pathname === '/me' ||
			pathname.startsWith('/users/')
		)
	}
	return pathname === target || pathname.startsWith(`${target}/`)
}

const primaryNavBaseClassName =
	'ring-ring ring-offset-background inline-flex h-9 items-center justify-center rounded-md text-sm font-medium ring-offset-2 outline-hidden transition-colors focus-visible:ring-2'
const primaryNavTextItemClassName = 'px-2 md:px-3'
const primaryNavIconItemClassName = 'size-9 rounded-full'

function getPrimaryNavItemClassName(isActive: boolean, isIcon: boolean) {
	return cn(
		primaryNavBaseClassName,
		isIcon ? primaryNavIconItemClassName : primaryNavTextItemClassName,
		isActive
			? 'bg-primary text-primary-foreground hover:bg-primary/80'
			: 'hover:bg-accent hover:text-accent-foreground text-foreground/80',
	)
}

function PrimaryNavLink({
	to,
	label,
	isActive,
	iconName,
}: {
	to: string
	label: string
	isActive: boolean
	iconName?: 'gear'
}) {
	return (
		<Link
			to={to}
			aria-label={label}
			aria-current={isActive ? 'page' : undefined}
			className={getPrimaryNavItemClassName(isActive, Boolean(iconName))}
		>
			{iconName ? <Icon name={iconName} size="md" aria-hidden="true" /> : label}
		</Link>
	)
}

function PrimaryNavItems({ pathname }: { pathname: string }) {
	return (
		<>
			<PrimaryNavLink
				to={STUDY_HOME_PATH}
				label="Studiu"
				isActive={isActiveNav(pathname, STUDY_HOME_PATH)}
			/>
			<PrimaryNavLink
				to={PROGRESS_HOME_PATH}
				label="Lecții"
				isActive={isActiveNav(pathname, PROGRESS_HOME_PATH)}
			/>
			<PrimaryNavLink
				to={PROFILE_HOME_PATH}
				label="Setări"
				isActive={isActiveNav(pathname, PROFILE_HOME_PATH)}
				iconName="gear"
			/>
		</>
	)
}

export type SiteNavbarProps = {
	/**
	 * Optional title shown centered in the navbar on md+ screens, and above the
	 * mobile nav. Used by the lesson view in the app layout.
	 */
	lessonTitle?: string | null
	/**
	 * When there's no authenticated user, render an "Autentificare" CTA.
	 * Set to `false` on the auth pages themselves (login/signup/etc.), where
	 * the CTA would be redundant. Defaults to `true`.
	 */
	showAuthCta?: boolean
}

export function SiteNavbar({
	lessonTitle = null,
	showAuthCta = true,
}: SiteNavbarProps = {}) {
	const rootData = useRouteLoaderData<typeof rootLoader>('root')
	const user = useOptionalUser()
	const location = useLocation()
	const logoHref = user ? STUDY_HOME_PATH : '/?landing'

	return (
		<header className="surface-1 border-border/80 sticky top-0 z-40 border-b backdrop-blur">
			{/* Matches the app layout's shared 60rem page frame (same max-width and
			    paddings), so the logo and menu sit exactly on the content's edges. */}
			<div className="mx-auto w-full max-w-[60rem] px-4 py-3 md:px-6 lg:px-8">
				<div
					className={cn(
						'flex min-h-9 items-center justify-between gap-2 md:gap-4',
						lessonTitle &&
							'md:grid md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] md:items-center md:gap-6',
					)}
				>
					<div
						className={cn(
							'flex min-w-0 items-center gap-4 lg:gap-6',
							lessonTitle ? 'flex-1 md:flex-none' : 'flex-1',
						)}
					>
						<Link to={logoHref} className="group inline-flex shrink-0">
							<span className="font-display text-foreground text-lg font-bold tracking-tight transition group-hover:opacity-80 md:text-xl">
								Matematix
							</span>
						</Link>
					</div>
					{lessonTitle ? (
						<div className="hidden min-w-0 items-center justify-center md:flex">
							<p className="text-foreground/90 max-w-[min(48vw,40rem)] truncate text-center font-sans text-base font-semibold tracking-tight lg:text-lg">
								{lessonTitle}
							</p>
						</div>
					) : null}
					{user ? (
						<div
							className={cn(
								'flex shrink-0 items-center gap-2 md:gap-3',
								lessonTitle && 'md:justify-self-end',
							)}
						>
							<nav
								className="flex items-center gap-1 md:gap-3"
								aria-label="Navigare principală"
							>
								<PrimaryNavItems pathname={location.pathname} />
							</nav>
							<Form action="/logout" method="POST">
								<button
									type="submit"
									aria-label="Deconectare"
									title="Deconectare"
									className={getPrimaryNavItemClassName(false, true)}
								>
									<Icon name="exit" size="md" aria-hidden="true" />
								</button>
							</Form>
						</div>
					) : (
						<div
							className={cn(
								'flex shrink-0 items-center gap-2',
								lessonTitle && 'md:justify-self-end',
							)}
						>
							{showAuthCta ? (
								<Button asChild size="sm">
									<Link to="/login">Autentificare</Link>
								</Button>
							) : null}
							<ThemeSwitch
								userPreference={rootData?.requestInfo.userPrefs.theme}
							/>
						</div>
					)}
				</div>

				{lessonTitle ? (
					<div className="mt-3 md:hidden">
						<p className="text-foreground/90 truncate font-sans text-sm font-semibold tracking-tight">
							{lessonTitle}
						</p>
					</div>
				) : null}
			</div>
		</header>
	)
}
